import React, { useState } from "react";
import {
  FaEye,
  FaTimes,
  FaExpandArrowsAlt,
  FaCompressArrowsAlt,
  FaSearchPlus,
  FaSearchMinus,
  FaUndo,
  FaDownload,
  FaRedoAlt,
  FaUndoAlt,
} from "react-icons/fa";

const DocumentImageViewer = ({ imageUrl, imageName, isOpen, onClose }) => {
  const [rotation, setRotation] = useState(0);
  const [scale, setScale] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Handle keyboard shortcuts
  React.useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isOpen) return;

      switch (e.key.toLowerCase()) {
        case "escape":
          onClose();
          break;
        case "f":
          if (!e.ctrlKey && !e.metaKey) {
            e.preventDefault();
            setIsFullscreen(!isFullscreen);
          }
          break;
        case "r":
          e.preventDefault();
          setRotation((prev) => prev + 90);
          break;
        case "q":
          e.preventDefault();
          setRotation((prev) => prev - 90);
          break;
        case "=":
        case "+":
          e.preventDefault();
          setScale((prev) => Math.min(prev * 1.2, 3));
          break;
        case "-":
          e.preventDefault();
          setScale((prev) => Math.max(prev / 1.2, 0.25));
          break;
        case "0":
          e.preventDefault();
          setScale(1);
          setRotation(0);
          setPosition({ x: 0, y: 0 });
          break;
        default:
          break;
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleKeyPress);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyPress);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, isFullscreen, scale, rotation, onClose]);

  if (!isOpen || !imageUrl) return null;

  const handleRotateLeft = () => {
    setRotation((prev) => prev - 90);
  };

  const handleRotateRight = () => {
    setRotation((prev) => prev + 90);
  };

  const handleZoomIn = () => {
    setScale((prev) => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setScale((prev) => Math.max(prev - 0.25, 0.25));
  };

  const handleReset = () => {
    setRotation(0);
    setScale(1);
    setPosition({ x: 0, y: 0 });
  };

  // Mouse wheel zoom
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY * -0.001;
    const newScale = Math.max(0.25, Math.min(3, scale + delta));
    setScale(newScale);
  };

  // Mouse drag handlers
  const handleMouseDown = (e) => {
    if (e.button === 0) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y,
      });
    }
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleToggleFullscreen = () => {
    if (!isFullscreen) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
    setIsFullscreen(!isFullscreen);
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = imageUrl;
    link.download = imageName || "document-image";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleClose = () => {
    handleReset();
    setIsDragging(false);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center"
      data-document-viewer
    >
      <div className="relative w-full h-full flex flex-col">
        {/* Header with controls */}
        <div className="flex items-center justify-between p-4 bg-black bg-opacity-50">
          <div className="flex items-center space-x-4">
            <h3 className="text-white text-lg font-semibold">
              {imageName || "Document Image"}
            </h3>
            <div className="flex items-center space-x-2 bg-gray-800 rounded-lg p-2">
              <span className="text-white text-sm">
                Zoom: {Math.round(scale * 100)}%
              </span>
              <span className="text-white text-sm">|</span>
              <span className="text-white text-sm">Rotation: {rotation}°</span>
            </div>
          </div>

          {/* Control buttons */}
          <div className="flex items-center space-x-2">
            {/* Zoom controls */}
            <button
              onClick={handleZoomOut}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Zoom Out"
            >
              <FaSearchMinus />
            </button>
            <button
              onClick={handleZoomIn}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Zoom In"
            >
              <FaSearchPlus />
            </button>

            {/* Rotation controls */}
            <button
              onClick={handleRotateLeft}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Rotate Left"
            >
              <FaUndoAlt />
            </button>
            <button
              onClick={handleRotateRight}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Rotate Right"
            >
              <FaRedoAlt />
            </button>

            {/* Reset */}
            <button
              onClick={handleReset}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title="Reset"
            >
              <FaUndo />
            </button>

            {/* Fullscreen toggle */}
            <button
              onClick={handleToggleFullscreen}
              className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-lg transition-colors"
              title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
            >
              {isFullscreen ? <FaCompressArrowsAlt /> : <FaExpandArrowsAlt />}
            </button>

            {/* Download */}
            {/* <button
              onClick={handleDownload}
              className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-lg transition-colors"
              title="Download Image"
            >
              <FaDownload />
            </button> */}

            {/* Close */}
            <button
              onClick={handleClose}
              className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-lg transition-colors"
              title="Close"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Image container */}
        <div
          className="flex-1 flex items-center justify-center p-4 overflow-hidden"
          onWheel={handleWheel}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          style={{ cursor: isDragging ? "grabbing" : "grab" }}
        >
          <div
            className="transition-transform duration-200 ease-out select-none"
            style={{
              transform: `translate(${position.x}px, ${position.y}px) rotate(${rotation}deg) scale(${scale})`,
              maxWidth: "100%",
              maxHeight: "100%",
            }}
            onMouseDown={handleMouseDown}
          >
            <img
              src={imageUrl}
              alt={imageName || "Document"}
              className="max-w-full max-h-full object-contain shadow-2xl pointer-events-none"
              style={{
                filter: "contrast(1.1) brightness(1.05)",
              }}
              draggable={false}
            />
          </div>
        </div>

        {/* Footer with keyboard shortcuts */}
        <div className="bg-black bg-opacity-50 p-2">
          <div className="flex flex-wrap justify-center gap-4 text-white text-xs">
            <span>🔍 Mouse wheel to zoom</span>
            <span>🖱️ Drag to pan</span>
            <span>ESC to close</span>
            <span>R to rotate right</span>
            <span>Q to rotate left</span>
            <span>F for fullscreen</span>
            <span>+/- to zoom</span>
            <span>0 to reset</span>
          </div>
        </div>
      </div>
    </div>
  );
};

// Enhanced image component with view button
export const DocumentImage = ({
  imageUrl,
  imageName,
  alt,
  className = "",
  showViewButton = true,
  children,
}) => {
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  const handleViewClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsViewerOpen(true);
  };

  if (!imageUrl) {
    return (
      <div className="relative">
        <div
          className={`bg-gray-100 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center w-72 h-44 ${className}`}
        >
          <span className="text-gray-500 text-sm mb-2">No image available</span>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <div className="relative group">
        <img
          src={imageUrl}
          alt={alt || imageName || "Document"}
          className={`w-72 h-44 object-cover border rounded shadow transition-all duration-300 group-hover:shadow-lg ${className}`}
        />

        {showViewButton && (
          <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center pointer-events-none">
            <button
              onClick={handleViewClick}
              className="opacity-0 group-hover:opacity-100 bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full transition-all duration-300 transform scale-90 group-hover:scale-100 pointer-events-auto"
              title="View Full Size"
            >
              <FaEye className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {children}

      <DocumentImageViewer
        imageUrl={imageUrl}
        imageName={imageName}
        isOpen={isViewerOpen}
        onClose={() => setIsViewerOpen(false)}
      />
    </div>
  );
};

export default DocumentImageViewer;
